import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

type LatexTokenKind = 'plain' | 'cmd' | 'brace' | 'dollars' | 'comment';

type LatexToken = {
  text: string;
  kind: LatexTokenKind;
}

const tokenizeLatexCode = (text: string): LatexToken[] => {
  const regex = /(\\[a-zA-Z@]+|\\.|[{}]|\$\$?)/g;
  const tokens: LatexToken[] = [];
  let last = 0;
  let match: RegExpExecArray | null = regex.exec(text);

  while (match) {
    const idx = match.index;
    const m = match[0];
    if (idx > last) tokens.push({ text: text.slice(last, idx), kind: 'plain' });
    if (m === '{' || m === '}') tokens.push({ text: m, kind: 'brace' });
    else if (m === '$' || m === '$$') tokens.push({ text: m, kind: 'dollars' });
    else tokens.push({ text: m, kind: 'cmd' });
    last = idx + m.length;
    match = regex.exec(text);
  }
  if (last < text.length) tokens.push({ text: text.slice(last), kind: 'plain' });
  return tokens;
};

const tokenizeLatexLine = (line: string): LatexToken[] => {
  const commentIndex = line.indexOf('%');
  if (commentIndex === -1) return tokenizeLatexCode(line);
  return [
    ...tokenizeLatexCode(line.slice(0, commentIndex)),
    { text: line.slice(commentIndex), kind: 'comment' }
  ];
};

type MinimalLatexEditorProps = {
  name: string;
  displayName?: string;
  helpText?: string;
  value: string;
  touched?: boolean;
  error?: string;
  onChange: (value: string) => void;
  livePreview?: React.ReactNode;
  attachments?: { id: number; titolo?: string }[];
  onBlur: () => void;
}

const MinimalLatexEditor = ({
  name, displayName, helpText, value, touched, error, onChange, onBlur, livePreview, attachments = [],
}: MinimalLatexEditorProps) => {
  const linePx = 20;
  const editorFontSize = '13px';
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const previewRef = useRef<HTMLPreElement | null>(null);
  const gutterRef = useRef<HTMLDivElement | null>(null);
  const [lineHeights, setLineHeights] = useState<number[]>([]);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [figureInsertion, setFigureInsertion] = useState<{ start: number; end: number; id: number } | null>(null);
  const pendingSelection = useRef<{ start: number; end: number } | null>(null);
  const invalid = !!(touched && error);
  const lineCount = Math.max(1, value.split('\n').length);
  const label = displayName || name.split('.').slice(-1)[0];
  const editorHeight = Math.max(160, lineCount * linePx + 200);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setIsDarkTheme(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useLayoutEffect(() => {
    const preview = previewRef.current;
    if (!preview) return;
    const measureLines = () => {
      const heights = Array.from(preview.querySelectorAll<HTMLElement>('[data-editor-line]'))
        .map(line => Math.max(linePx, line.getBoundingClientRect().height));
      setLineHeights(previous => previous.length === heights.length && previous.every((height, index) => height === heights[index])
        ? previous : heights);
    };
    measureLines();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureLines) : null;
    observer?.observe(preview);
    window.addEventListener('resize', measureLines);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measureLines);
    };
  }, [value]);

  useLayoutEffect(() => {
    if (!pendingSelection.current || !textareaRef.current) return;
    const { start, end } = pendingSelection.current;
    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(start, end);
    pendingSelection.current = null;
    syncScroll();
  }, [value]);

  const insertFormatting = (kind: 'bold' | 'italic' | 'itemize' | 'enumerate') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end);
    let replacement: string;
    let selectionStart: number;
    let selectionEnd: number;
    if (kind === 'bold' || kind === 'italic') {
      const prefix = kind === 'bold' ? '\\textbf{' : '\\textit{';
      const content = selected || 'testo';
      replacement = prefix + content + '}';
      selectionStart = start + prefix.length;
      selectionEnd = selectionStart + content.length;
    } else {
      const leading = start > 0 && value[start - 1] !== '\n' ? '\n' : '';
      const trailing = end < value.length && value[end] !== '\n' ? '\n' : '';
      const items = (selected || 'Elemento').split('\n').map(line => `  \\item ${line}`);
      const prefix = `${leading}\\begin{${kind}}\n`;
      replacement = `${prefix}${items.join('\n')}\n\\end{${kind}}${trailing}`;
      selectionStart = start + prefix.length + '  \\item '.length;
      selectionEnd = selectionStart + (selected ? selected.split('\n')[0].length : 'Elemento'.length);
    }
    pendingSelection.current = { start: selectionStart, end: selectionEnd };
    onChange(value.slice(0, start) + replacement + value.slice(end));
  };

  const insertFigure = () => {
    if (!figureInsertion || !attachments.some(attachment => attachment.id === figureInsertion.id)) return;
    const { start, end, id } = figureInsertion;
    const markup = `[figura: 0.3](/attachments/${id}/)`;
    const caret = start + markup.length;
    pendingSelection.current = { start: caret, end: caret };
    setFigureInsertion(null);
    onChange(value.slice(0, start) + markup + value.slice(end));
  };

  const figureTitle = attachments.length ? 'Inserisci figura' : 'Allega un file prima di premere questo bottone!';

  const palette = isDarkTheme ? {
    frameBg: '#111827',
    frameBorder: '#374151',
    gutterBg: '#0f172a',
    gutterBorder: '#334155',
    gutterText: '#cbd5e1',
    text: '#e5e7eb',
    caret: '#f8fafc',
    cmd: '#60a5fa',
    braces: '#fbbf24',
    dollars: '#a78bfa',
    comment: '#4ade80',
  } : {
    frameBg: '#ffffff',
    frameBorder: '#94a3b8',
    gutterBg: '#f1f5f9',
    gutterBorder: '#cbd5e1',
    gutterText: '#1e293b',
    text: '#0f172a',
    caret: '#0f172a',
    cmd: '#1d4ed8',
    braces: '#9a3412',
    dollars: '#6d28d9',
    comment: '#166534',
  };

  const highlightedLines = (value || ' ').split('\n').map(line => tokenizeLatexLine(line));

  const syncScroll = () => {
    if (!textareaRef.current) return;
    if (previewRef.current) {
      previewRef.current.scrollTop = textareaRef.current.scrollTop;
      previewRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
    if (gutterRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  return (
    <div className="form-group mb-3" style={{ '--latex-frame-border': palette.frameBorder } as React.CSSProperties}>
      <div role="toolbar" aria-label={`Formattazione ${label}`} className="d-flex flex-wrap mb-2">
        {([
          ['bold', 'Grassetto', <strong key="bold">B</strong>],
          ['italic', 'Corsivo', <em key="italic">I</em>],
          ['itemize', 'Elenco puntato', <span key="itemize"><i className="fa fa-list-ul" aria-hidden="true" /> Elenco</span>],
          ['enumerate', 'Elenco numerato', <span key="enumerate"><i className="fa fa-list-ol" aria-hidden="true" /> Numerato</span>],
        ] as const).map(([kind, title, content]) => (
          <button key={kind} type="button" className="btn btn-sm btn-outline-secondary mr-1 mb-1"
            title={title} aria-label={title}
            onMouseDown={e => e.preventDefault()}
            onClick={() => insertFormatting(kind)}>{content}</button>
        ))}
        <span title={figureTitle}>
          <button type="button" className="btn btn-sm btn-outline-secondary mr-1 mb-1"
            disabled={!attachments.length} title={figureTitle} aria-label="Inserisci figura"
            aria-expanded={!!figureInsertion && attachments.some(attachment => attachment.id === figureInsertion.id)}
            aria-controls={`${name}-figure-picker`}
            onMouseDown={e => e.preventDefault()}
            onClick={() => {
              const textarea = textareaRef.current;
              if (textarea && attachments.length) setFigureInsertion(figureInsertion ? null : {
                start: textarea.selectionStart, end: textarea.selectionEnd, id: attachments[0].id,
              });
            }}>
            <i className={`fa ${attachments.length ? 'fa-image' : 'fa-exclamation-triangle'}`} aria-hidden="true" /> Figura
          </button>
        </span>
      </div>
      {figureInsertion && attachments.some(attachment => attachment.id === figureInsertion.id) &&
        <div id={`${name}-figure-picker`} className="border rounded p-2 mb-2">
          <label htmlFor={`${name}-figure-attachment`}>Allegato da inserire</label>
          <select id={`${name}-figure-attachment`} className="form-control mb-2" autoFocus
            value={figureInsertion.id}
            onChange={e => setFigureInsertion({ ...figureInsertion, id: Number(e.target.value) })}>
            {attachments.map(attachment => <option key={attachment.id} value={attachment.id}>
              {attachment.titolo || `Allegato ${attachment.id}`} (#{attachment.id})
            </option>)}
          </select>
          <button type="button" className="btn btn-sm btn-primary mr-2" onClick={insertFigure}>Inserisci</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
            setFigureInsertion(null);
            textareaRef.current?.focus();
          }}>Annulla</button>
        </div>
      }
      <div className="row align-items-stretch">
        <div className={livePreview ? 'col-12 col-lg-6 d-flex flex-column' : 'col-12 d-flex flex-column'} style={{ minWidth: 0 }}>
          <div
            className="d-flex rounded flex-grow-1"
            style={{
              border: `1px solid ${invalid ? '#dc3545' : palette.frameBorder}`,
              background: palette.frameBg,
            }}
          >
            <div
              ref={gutterRef}
              style={{
                position: 'relative',
                zIndex: 3,
                width: '56px',
                minWidth: '56px',
                overflow: 'hidden',
                borderRight: `1px solid ${palette.gutterBorder}`,
                background: palette.gutterBg,
                color: palette.gutterText,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: editorFontSize,
                lineHeight: `${linePx}px`,
                textAlign: 'right',
                padding: '10px 6px',
                userSelect: 'none',
                minHeight: editorHeight,
                height: 'auto',
              }}
            >
              {Array.from({ length: lineCount }, (_, i) => (
                <div key={i} style={{ height: lineHeights[i] || linePx }}>{i + 1}</div>
              ))}
            </div>
            <div style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: editorHeight, overflow: 'hidden', zIndex: 1 }}>
              <pre
                ref={previewRef}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  margin: 0,
                  padding: '10px 12px',
                  background: palette.frameBg,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: editorFontSize,
                  lineHeight: `${linePx}px`,
                  height: '100%',
                  minHeight: editorHeight,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  scrollbarGutter: 'stable',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  pointerEvents: 'none',
                  color: palette.text,
                }}
              >
                {highlightedLines.map((line, lineIdx) => (
                  <span key={lineIdx} data-editor-line style={{ display: 'block', minHeight: linePx }}>
                    {line.map((token, tokenIdx) => {
                      if (token.kind === 'plain') return <React.Fragment key={tokenIdx}>{token.text}</React.Fragment>;
                      const color = token.kind === 'cmd'
                        ? palette.cmd
                        : token.kind === 'brace'
                          ? palette.braces
                          : token.kind === 'dollars'
                            ? palette.dollars
                            : palette.comment;
                      return <span key={tokenIdx} style={{ color }}>{token.text}</span>;
                    })}
                  </span>
                ))}
              </pre>
              <textarea
                ref={textareaRef}
                id={name}
                name={name}
                aria-label={label}
                value={value}
                onChange={e => { setFigureInsertion(null); onChange(e.target.value); }}
                onBlur={onBlur}
                onScroll={syncScroll}
                aria-invalid={invalid}
                aria-describedby={invalid ? `${name}-error` : undefined}
                wrap="soft"
                style={{
                  position: 'absolute',
                  zIndex: 2,
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  resize: 'none',
                  border: 'none',
                  outline: 'none',
                  padding: '10px 12px',
                  background: 'transparent',
                  color: 'transparent',
                  caretColor: palette.caret,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: editorFontSize,
                  lineHeight: `${linePx}px`,
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  scrollbarGutter: 'stable',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              />
            </div>
          </div>
        </div>
        {livePreview && <div className="col-12 col-lg-6 d-flex flex-column mt-3 mt-lg-0" style={{ minWidth: 0, minHeight: editorHeight, '--latex-preview-min-height': `${editorHeight}px` } as React.CSSProperties}>{livePreview}</div>}
      </div>
      {helpText && <small className="form-text text-muted">{helpText}</small>}
      {invalid && <div id={`${name}-error`} className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

export default MinimalLatexEditor;
